/**
 * Página /superadmin/pagos-iniciales — Sprint 15 S15-03.
 *
 * Importador CSV matricial de pagos iniciales para carga LIVE. Pega o
 * sube el contenido de la planilla del tesorero (Calbucura_mayo_2026.xlsx
 * exportada a CSV) y el sistema crea cuotas de arrastre 2025 + pagos
 * sintéticos mensuales 2026 + saldos a favor 2025.
 *
 * Flujo:
 * 1. Operador pega CSV en el textarea o sube archivo.
 * 2. Botón "Vista previa" → POST con dry_run=true → muestra métricas
 *    sin persistir.
 * 3. Si está OK, botón "Confirmar import" → POST con dry_run=false →
 *    persiste y muestra resumen final.
 *
 * Comportamiento documentado en docstring del service backend
 * `importar_pagos_iniciales_csv()`.
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  generarPlantillaCsv,
  importarPagosIniciales,
  type ImportarPagosInicialesResponse,
} from '../../lib/api/import_pagos_iniciales';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/layout/PageHeader';

export function PagosInicialesPage() {
  const [csv, setCsv] = useState('');
  const [resultado, setResultado] =
    useState<ImportarPagosInicialesResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function handleDescargarPlantilla() {
    const contenido = generarPlantillaCsv();
    const blob = new Blob([contenido], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla-pagos-iniciales.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function handleArchivoSeleccionado(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const texto = await file.text();
    setCsv(texto);
    setResultado(null);
    setError(null);
  }

  async function ejecutarImport(dryRun: boolean) {
    if (!csv.trim()) {
      setError('Pega o sube un CSV antes de continuar.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setResultado(null);
    try {
      const res = await importarPagosIniciales({
        csv_contenido: csv,
        dry_run: dryRun,
      });
      setResultado(res);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Carga inicial LIVE"
        title="Pagos iniciales"
        description="Importador CSV matricial de la planilla del tesorero. Carga deudas 2025, abonos parciales, saldos a favor y pagos mensuales 2026 del barrio."
        action={
          <Button variant="ghost" onClick={handleDescargarPlantilla}>
            Descargar plantilla CSV
          </Button>
        }
      />

      <div className="bg-bg-elevated border-l-4 border-admin-red rounded-sm p-4 max-w-3xl text-sm text-text-secondary space-y-2">
        <div className="font-medium text-text-primary">Antes de importar:</div>
        <ol className="list-decimal list-inside space-y-1 pl-2">
          <li>Casas + personas + tenencias deben estar cargados.</li>
          <li>
            Cuotas semestrales <code className="text-text-primary">2026-S1</code>{' '}
            y <code className="text-text-primary">2026-S2</code> deben estar
            generadas (POST <code>/admin/cuotas/generar</code>).
          </li>
          <li>
            Cada lote debe tener tenencia con{' '}
            <code className="text-text-primary">responsable_pago_cuotas=true</code>{' '}
            vigente.
          </li>
        </ol>
        <p className="text-xs text-text-muted pt-1">
          Usa "Vista previa" primero (modo dry-run) para validar sin persistir
          cambios.
        </p>
      </div>

      <div className="space-y-3 max-w-4xl">
        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Contenido CSV (pega o sube archivo)
          </span>
          <textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            rows={10}
            placeholder="casa,deuda_2025,a_deuda,ene,feb,mar,...&#10;6931,0,,5000,5000,..."
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary font-mono text-xs"
          />
        </label>

        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="file"
            accept=".csv,text/csv"
            ref={fileInputRef}
            onChange={handleArchivoSeleccionado}
            className="hidden"
          />
          <Button
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting}
          >
            Subir archivo CSV
          </Button>
          <Button
            variant="ghost"
            onClick={() => ejecutarImport(true)}
            disabled={submitting || !csv.trim()}
          >
            {submitting ? 'Procesando…' : 'Vista previa (dry-run)'}
          </Button>
          <Button
            variant="primary"
            onClick={() => ejecutarImport(false)}
            disabled={submitting || !csv.trim()}
          >
            {submitting ? 'Procesando…' : 'Confirmar import'}
          </Button>
        </div>

        {error && (
          <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
            {error}
          </div>
        )}
      </div>

      {resultado && <ResultadoImport resultado={resultado} />}
    </div>
  );
}

function ResultadoImport({
  resultado,
}: {
  resultado: ImportarPagosInicialesResponse;
}) {
  const huboCambios =
    resultado.cuotas_arrastre_creadas +
      resultado.pagos_abono_deuda_creados +
      resultado.pagos_saldo_favor_creados +
      resultado.pagos_mensuales_creados >
    0;
  const colorBanner =
    resultado.modo === 'real' && huboCambios
      ? 'border-state-success bg-state-success/10'
      : 'border-admin-red bg-bg-elevated';

  return (
    <div className="space-y-4 max-w-4xl">
      <div
        className={`border-l-4 rounded-sm p-4 ${colorBanner} text-text-primary`}
      >
        <div className="text-xs uppercase tracking-wide font-medium mb-2">
          {resultado.modo === 'dry_run' ? '🔍 Vista previa (no se persistió nada)' : '✅ Import confirmado'}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-sm">
          <Metrica label="Filas procesadas" value={resultado.filas_procesadas} />
          <Metrica
            label="Filas con error"
            value={resultado.filas_con_error}
            highlight={resultado.filas_con_error > 0}
          />
          <Metrica
            label="Cuotas arrastre creadas"
            value={resultado.cuotas_arrastre_creadas}
          />
          <Metrica
            label="Pagos abono a deuda"
            value={resultado.pagos_abono_deuda_creados}
          />
          <Metrica
            label="Pagos saldo a favor"
            value={resultado.pagos_saldo_favor_creados}
          />
          <Metrica
            label="Pagos mensuales 2026"
            value={resultado.pagos_mensuales_creados}
          />
          <Metrica
            label="Distribuciones totales"
            value={resultado.distribuciones_creadas}
          />
          <Metrica
            label="Skips defensivos"
            value={resultado.skips_defensivos.length}
          />
        </div>
      </div>

      {resultado.errores.length > 0 && (
        <div className="bg-state-danger/10 border-l-4 border-state-danger rounded-sm p-3 text-sm">
          <div className="font-medium text-text-primary mb-1">
            Errores por fila ({resultado.errores.length}):
          </div>
          <ul className="space-y-0.5 text-text-secondary text-xs">
            {resultado.errores.map((err, i) => (
              <li key={i}>
                Fila {err.numero_fila}
                {err.casa ? ` (Casa ${err.casa})` : ''}: {err.motivo}
              </li>
            ))}
          </ul>
        </div>
      )}

      {resultado.skips_defensivos.length > 0 && (
        <div className="bg-state-warning/10 border-l-4 border-state-warning rounded-sm p-3 text-sm">
          <div className="font-medium text-text-primary mb-1">
            Skips defensivos ({resultado.skips_defensivos.length}):
          </div>
          <p className="text-xs text-text-muted mb-1">
            Pagos de carga_inicial que ya existían y fueron omitidos. Si
            quieres corregirlos, anúlalos desde <code>/tesoreria/pagos</code> y
            re-importa.
          </p>
          <ul className="space-y-0.5 text-text-secondary text-xs">
            {resultado.skips_defensivos.map((s, i) => (
              <li key={i}>
                Fila {s.numero_fila} (Casa {s.casa}): {s.detalle}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Metrica({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-text-muted">{label}</div>
      <div
        className={`text-lg font-medium ${
          highlight && value > 0 ? 'text-state-danger' : 'text-text-primary'
        }`}
      >
        {value}
      </div>
    </div>
  );
}
